/* eslint-disable */
import { extractNumeric } from "@/Pages/AIFMSUMMARY/helper";
import { useCallback, useEffect, useMemo, useState, memo } from "react";
import issued from '@/Images/issued.svg';
import produced from '@/Images/produced.svg';
import primaryPackaged from '@/Images/primaryPack.svg';
import secondaryPackaged from '@/Images/secondaryPack.svg';
import dispatched from '@/Images/externalDispatch.svg';
import wastage from '@/Images/wastage.svg';

const imagesMap = {
    'issued': issued,
    'produced': produced,
    'primaryPackaged': primaryPackaged,
    'secondaryPackaged': secondaryPackaged,
    'dispatch': dispatched,
    'wastage': wastage
}

const formatDualValue = (value, hideUnit = false) => {
    if (typeof value !== 'string') return value;

    const renderFraction = (val) => {
        if (!val.includes('/')) return val;
        const parts = val.split('/');
        if (parts.length !== 2) return val;

        let numerator = parts[0].trim();
        const originalNumerator = parts[0].trim();
        numerator = extractNumeric(numerator);
        if (isNaN(parseFloat(numerator))) return val;

        const denominatorPart = parts[1].trim();
        const lastSpaceIndex = denominatorPart.lastIndexOf(' ');
        let denominator = denominatorPart;
        let unit = '';

        if (lastSpaceIndex !== -1) {
            denominator = denominatorPart.substring(0, lastSpaceIndex);
            unit = denominatorPart.substring(lastSpaceIndex + 1);
        }

        return (
            <span className='inline-flex items-center gap-1.5 px-1 py-0.5 rounded hover:bg-gray-50/50 transition-colors align-middle'>
                <span className='flex flex-col items-center justify-center min-w-[20px]'>
                    <span className='border-b border-current text-[14px] font-semibold leading-none pb-[1px] w-full text-center tracking-tight'>{originalNumerator}</span>
                    <span className='text-[14px] text-gray-600 font-medium leading-none pt-[1px] w-full text-center tracking-tight'>{denominator}</span>
                </span>
                {!hideUnit && unit && <span className='text-[14px] text-gray-600 font-semibold uppercase tracking-wide self-center'>{unit}</span>}
            </span>
        );
    };

    if (value.includes('→')) {
        return (
            <span className="inline-flex items-center gap-1">
                {value.split('→').map((part, index, array) => (
                    <span key={index} className="inline-flex items-center gap-1">
                        {renderFraction(part.trim())}
                        {index < array.length - 1 && <span className="text-gray-400 font-bold select-none text-[12px] leading-none mb-0.5">→</span>}
                    </span>
                ))}
            </span>
        );
    }

    return renderFraction(value);
};

const CellChips = memo(({ value }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    if (typeof value !== 'string' || !value.includes(',')) return value;

    const splitValue = value.split(',');
    if (splitValue.length === 0) return value;

    const visibleChips = isExpanded ? splitValue : splitValue.slice(0, 2);
    const hasMore = splitValue.length > 2;

    return (
        <div className='flex flex-wrap gap-1'>
            {visibleChips.map((val, idx) => (
                <span key={idx} className='inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-[#FDE6AD] text-gray-800 border border-[#00000033] shadow-sm'>
                    {val.trim()}
                </span>
            ))}
            {hasMore && (
                <span
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsExpanded(!isExpanded);
                    }}
                    className='inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-100 text-blue-800 border border-blue-300 shadow-sm cursor-pointer hover:bg-blue-200 transition-colors'
                >
                    {isExpanded ? 'Show Less' : `+${splitValue.length - 2} more`}
                </span>
            )}
        </div>
    );
});

const shouldShowRupee = (value) => {
    if (value === null || value === undefined) return false;
    const strVal = String(value).trim();
    if (strVal === '-' || strVal === '') return false;
    if (strVal.includes('₹')) return false;
    if (!/\d/.test(strVal)) return false;
    return true;
};

export const useTableData = (rawData, parentTab, search, isLoading, selectedTab) => {
    const isCostTab = selectedTab === 'cost';
    const [columns, setColumns] = useState([]);
    const [rows, setRows] = useState([]);

    const getChips = useCallback((value) => {
        return <CellChips value={value} />;
    }, []);

    useEffect(() => {
        if (isLoading) {
            setColumns([]);
            setRows([]);
        }
    }, [isLoading]);

    useEffect(() => {
        if (rawData?.columns?.length > 0 || rawData?.data?.length > 0) {
            setColumns(rawData?.columns);
            setRows(rawData?.data);
        }
    }, [rawData]);

    const getColumns = useMemo(() => {
        if (!columns?.length) return [];
        return columns.map((column, index) => {
            const isNameColumn = ['productName', 'itemName', 'ingredientName', 'intermediateName'].includes(column?.key);
            return {
                ...column,
                title: column?.label,
                searchable: column?.key === 'productName' ? true : column?.searchable,
                customCol: true,
                sortable: true,
                width: isNameColumn ? 220 : (columns?.length > 8 ? 110 : 130),
            };
        });
    }, [columns]);

    const filterData = useMemo(() => {
        if (!rows?.length) return [];
        if (!search?.searchValue) return rows;

        const searchLower = String(search?.searchValue).toLowerCase().trim();

        return rows.filter((item) => {
            const searchableText = Object.values(item)
                .map((value) => {
                    if (value === null || value === undefined) return '';

                    if (typeof value === 'object' && value !== null) {
                        if ('value' in value) {
                            return String(value.value || '');
                        }
                        return JSON.stringify(value);
                    }

                    return String(value);
                })
                .filter(Boolean)
                .join(' ')
                .toLowerCase();

            return searchableText.includes(searchLower);
        });

        // if (!search?.searchValue || !search?.value) return rows;
        // const searchLower = String(search?.searchValue).toLowerCase();
        // return rows.filter((item) => {
        //     const itemValue = String(item?.[search?.value] ?? '').toLowerCase();
        //     return itemValue.includes(searchLower);
        // });
    }, [rows, search?.searchValue, search?.value]);


    const customRowData = useMemo(() => {
        const base = {};
        getColumns.forEach((column, index) => {
            base[column.key] = (product) => {
                const cellData = product?.[column.key];
                const isDummy = product?.isDummy;
                const showDummyTag = isDummy && index === 0;

                if (cellData && typeof cellData === 'object' && cellData.value !== undefined) {
                    const color = cellData.extra?.color;
                    const colorClass = color === 'red' ? 'text-red-600' :
                        color === 'green' ? 'text-green-600' :
                            color === 'yellow' ? 'text-yellow-600' : 'text-gray-700';
                    return (
                        <div className={`flex flex-col items-center justify-center text-center text-[14px] ${cellData?.link ? 'cursor-pointer hover:underline text-blue-700' : colorClass}`}
                            {...(cellData?.link ? { onClick: () => window.open(cellData?.link, '_blank') } : {})}
                        >
                            {column?.chips ? getChips(cellData?.value) : (
                                <div className='flex items-center justify-center gap-2'>
                                    {isCostTab && index > 0 && shouldShowRupee(cellData.value) && <span className="text-[13px] font-semibold">₹</span>}
                                    {formatDualValue(cellData.value ?? '-', isCostTab)}
                                    {showDummyTag && (
                                        <span className="px-1.5 py-0.5 text-[10px] font-medium bg-[#0F766E] text-white rounded border border-gray-300">
                                            Dummy Data
                                        </span>
                                    )}
                                </div>
                            )}
                            {/* {cellData?.extra?.subText && (
                                <div className="text-[10px] text-gray-500 font-medium opacity-85">
                                    {cellData.extra.subText}
                                </div>
                            )} */}
                            {Array.isArray(cellData?.data) && cellData.data.map((item, idx) => (
                                <div key={idx} className={`flex gap-1 items-center`} style={{ color: item.color || '#6b7280', fontSize: ((item.size || 14) + 'px') }}>
                                    {item.label && <span className="font-semibold">{item.label}:</span>}
                                    <span>{item.value}</span>
                                </div>
                            ))}
                        </div>
                    );
                }
                return (
                    <div className="text-gray-700 text-center text-[14px]">
                        {column?.chips ? getChips(cellData ?? '-') : (
                            <span className="inline-flex items-center">
                                {isCostTab && index > 0 && shouldShowRupee(cellData) && <span className="mr-0.5">₹</span>}
                                {formatDualValue(cellData ?? '-', isCostTab)}
                            </span>
                        )}
                        {showDummyTag && (
                            <span className="ml-2 px-1.5 py-0.5 text-[10px] font-medium bg-[#0F766E] text-white rounded border border-gray-300">
                                Dummy Data
                            </span>
                        )}
                    </div>
                );
            };
        });
        return base;
    }, [getColumns, search, getChips, isCostTab]);

    const sortInfo = useMemo(() => {
        const info = {};
        getColumns.forEach((column) => {
            const isNameColumn = ['productName', 'itemName', 'ingredientName', 'intermediateName', 'unit'].includes(column.key);
            const detectedType = column?.dataType || (isNameColumn ? 'string' : 'number');

            info[column.key] = {
                type: detectedType,
                customValue: true,
                value: (obj) => {
                    const cellData = obj?.[column.key];
                    let rawValue = (cellData && typeof cellData === 'object') ? cellData.value : cellData;

                    if (rawValue === undefined || rawValue === null || rawValue === '') {
                        return detectedType === 'number' ? 0 : '';
                    }

                    if (typeof rawValue === 'string') {
                        if (rawValue.includes('/') || detectedType === 'number') {
                            const firstPart = rawValue.split('/')[0];
                            const extractedNum = extractNumeric(firstPart);
                            if (!isNaN(extractedNum)) return extractedNum;
                        }
                    } else if (typeof rawValue === 'number') {
                        return rawValue;
                    }

                    return rawValue ?? '';
                },
            };
        });
        return info;
    }, [getColumns]);


    const customCol = useCallback((column) => {
        return (
            <div
                className="flex flex-col gap-1"
            >
                <div className="flex items-center gap-2">
                    {column?.image && (
                        <img src={imagesMap[column?.image]} alt={column?.image} className="w-4 h-4" />
                    )}
                    <p className={`text-center w-full font-bold text-[11px] leading-tight break-words ${column?.link ? 'cursor-pointer hover:underline text-blue-700' : 'text-gray-700'}`}
                        {...(column?.link ? { onClick: () => window.open(column?.link, '_blank') } : {})}>{column?.label ?? ''}</p>
                </div>
                {Array.isArray(column?.data) && column?.data?.length > 0 && column?.data?.map((item, idx) => (
                    <div key={idx} className="flex flex-col">
                        <p className={`flex gap-1 justify-center m-0 text-center items-center ${item?.link ? 'cursor-pointer hover:underline text-blue-700' : 'text-gray-700'}`}
                            {...(item?.link ? { onClick: () => window.open(item?.link, '_blank') } : {})}
                            style={{ color: item.color || '#6b7280', fontSize: ((item.size || 10) + 'px') }}
                        >
                            {/* <span className="text-[11px]">{item?.label}</span> : <span className="text-[11px]">{item?.value}</span> */}
                            {item.label && <span className="font-semibold">{item.label}:</span>}
                            {formatDualValue(item.value)}
                        </p>
                    </div>
                ))}
            </div>
        );
    }, []);

    const getTableInfo = useCallback(() => {
        return {
            data: filterData,
            tableRows: customRowData,
            tableColumns: getColumns,
            sortInfo,
            customCol,
        };
    }, [rows, getColumns, customRowData, sortInfo, search, customCol, filterData]);

    const dropDownItems = useMemo(() => {
        if (!getColumns.length) return [];
        return getColumns
            .filter((column) => column?.searchable)
            .map((column) => ({
                label: column?.label,
                value: column?.key,
                placeholder: `Search by ${column?.label}`,
            }));
    }, [getColumns]);

    return {
        getTableInfo,
        dropDownItems
    };
}